import { motion } from "framer-motion";
import { useState } from "react";
import { Quote, Star, User, Play, X, Video, Building2 } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?]+)/);
  return match ? match[1] : null;
};

export const TestimonialsSection = ({ testimonials }) => {
  const [videoModal, setVideoModal] = useState({ open: false, url: null, title: null });

  const openVideo = (url, name) => {
    const videoId = getYouTubeVideoId(url);
    if (videoId) {
      setVideoModal({ 
        open: true, 
        url: `https://www.youtube.com/embed/${videoId}?autoplay=1`,
        title: `${name}'s Video Testimonial`
      });
    }
  };

  if (!testimonials || testimonials.length === 0) return null;

  // Check if any testimonial has video to determine if we need alignment
  const hasAnyVideo = testimonials.some(t => t.video_url);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="testimonials-section">
        {testimonials.map((testimonial, index) => {
          const hasVideo = !!testimonial.video_url;
          const videoId = hasVideo ? getYouTubeVideoId(testimonial.video_url) : null;
          
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.15 }}
              className={`relative rounded-2xl bg-white border shadow-sm overflow-hidden flex flex-col ${
                hasVideo ? 'border-red-100' : 'border-slate-200'
              }`}
              data-testid={`testimonial-card-${index}`}
            >
              {/* Top Banner Section - Same height for alignment */}
              {hasVideo && videoId ? (
                /* Video Thumbnail Banner */
                <button
                  onClick={() => openVideo(testimonial.video_url, testimonial.name)}
                  className="relative w-full group cursor-pointer flex-shrink-0"
                  data-testid={`testimonial-video-btn-${index}`}
                >
                  <div className="relative aspect-video w-full overflow-hidden bg-slate-900">
                    <img
                      src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                      alt={`${testimonial.name}'s video testimonial`}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300"
                    />
                    
                    {/* Dark Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent group-hover:from-black/60 transition-colors" />
                    
                    {/* Play Button - Center */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center shadow-xl group-hover:scale-110 group-hover:bg-red-500 transition-all duration-300">
                        <Play className="h-7 w-7 text-white fill-white ml-1" />
                      </div>
                    </div>
                    
                    {/* Watch Video Label */}
                    <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                      <div className="flex items-center gap-2 text-white">
                        <Video className="w-4 h-4" />
                        <span className="text-sm font-medium">Watch Video Testimonial</span>
                      </div>
                      <div className="flex items-center gap-1 px-2 py-1 rounded bg-white/20 backdrop-blur-sm">
                        <Play className="w-3 h-3 text-white" />
                        <span className="text-xs text-white">Click to Play</span>
                      </div>
                    </div>
                  </div>
                </button>
              ) : hasAnyVideo ? (
                /* Company Logo Banner - Aligned with video thumbnail */
                <div className="relative aspect-video w-full overflow-hidden bg-gradient-to-br from-[#0066CC] via-[#0052A3] to-[#003366] flex-shrink-0">
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{ 
                      backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'40\' height=\'40\' viewBox=\'0 0 40 40\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M20 20h20v20H20V20zM0 0h20v20H0V0z\'/%3E%3C/g%3E%3C/svg%3E")'
                    }} />
                  </div>
                  
                  {/* Company Info */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
                    {/* Company Logo/Icon */}
                    <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4 shadow-lg">
                      <Building2 className="w-10 h-10 text-white" />
                    </div>
                    
                    {/* Company Name */}
                    {testimonial.company && (
                      <h3 className="text-xl font-bold text-white text-center mb-1">
                        {testimonial.company}
                      </h3>
                    )}
                    
                    {/* Client Name */}
                    <p className="text-white/80 text-sm text-center">
                      Client Success Story
                    </p>
                  </div>
                  
                  {/* Quote Icon */}
                  <div className="absolute bottom-3 left-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <Quote className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Content Section */}
              <div className="p-6 flex-1 flex flex-col">
                {/* Stars */}
                <div className="flex gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>

                {/* Quote Text */}
                <blockquote className="text-slate-700 leading-relaxed mb-5 flex-1">
                  &ldquo;{testimonial.quote}&rdquo;
                </blockquote>

                {/* Author */}
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  {testimonial.image_url ? (
                    <img
                      src={testimonial.image_url}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-slate-100"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#0066CC] to-[#0052A3] flex items-center justify-center">
                      <User className="w-6 h-6 text-white" />
                    </div>
                  )}
                  
                  {/* Name & Details */}
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">
                      {testimonial.role && <span>{testimonial.role}</span>}
                      {testimonial.role && testimonial.company && <span> at </span>}
                      {testimonial.company && <span className="text-[#0066CC]">{testimonial.company}</span>}
                    </p>
                  </div>

                  {/* Video Badge - Compact indicator */}
                  {hasVideo && (
                    <button
                      onClick={() => openVideo(testimonial.video_url, testimonial.name)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors text-sm font-medium"
                    >
                      <Play className="w-3.5 h-3.5 fill-red-600" />
                      <span>Play</span>
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Video Modal */}
      <Dialog open={videoModal.open} onOpenChange={(open) => setVideoModal({ open, url: null, title: null })}>
        <DialogContent className="max-w-4xl p-0 bg-black overflow-hidden">
          {/* Close Button */}
          <button
            onClick={() => setVideoModal({ open: false, url: null, title: null })}
            className="absolute top-3 right-3 z-50 w-10 h-10 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center transition-colors"
          >
            <X className="h-5 w-5 text-white" />
          </button>
          
          {/* Video Title */}
          {videoModal.title && (
            <div className="absolute top-3 left-3 z-50 px-3 py-1.5 bg-black/50 backdrop-blur-sm rounded-lg">
              <p className="text-white text-sm font-medium">{videoModal.title}</p>
            </div>
          )}
          
          {videoModal.url && (
            <iframe
              src={videoModal.url}
              className="w-full aspect-video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Testimonial Video"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

// Compact horizontal carousel version for limited space
export const TestimonialsCarousel = ({ testimonials }) => {
  const [videoModal, setVideoModal] = useState({ open: false, url: null });

  const openVideo = (url) => {
    const videoId = getYouTubeVideoId(url);
    if (videoId) {
      setVideoModal({ open: true, url: `https://www.youtube.com/embed/${videoId}?autoplay=1` });
    }
  };

  if (!testimonials || testimonials.length === 0) return null;

  return (
    <>
      <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory" data-testid="testimonials-carousel">
        {testimonials.map((testimonial, index) => {
          const hasVideo = !!testimonial.video_url;
          
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className="flex-shrink-0 w-80 rounded-xl bg-white border border-slate-200 shadow-sm snap-start overflow-hidden"
            >
              {/* Video indicator bar */}
              {hasVideo && (
                <button
                  onClick={() => openVideo(testimonial.video_url)}
                  className="w-full px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 flex items-center justify-center gap-2 text-white text-sm font-medium hover:from-red-600 hover:to-red-700 transition-all"
                >
                  <Play className="w-4 h-4 fill-white" />
                  Watch Video Testimonial
                </button>
              )}
              
              <div className="p-5">
                {/* Stars */}
                <div className="flex gap-0.5 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                  ))}
                </div>

                {/* Quote */}
                <p className="text-slate-700 text-sm leading-relaxed mb-4 line-clamp-4">
                  &ldquo;{testimonial.quote}&rdquo;
                </p>

                {/* Author */}
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0066CC] to-[#0052A3] flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {testimonial.image_url ? (
                      <img src={testimonial.image_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                    ) : (
                      <span className="text-white text-sm font-medium">
                        {testimonial.name?.charAt(0) || "A"}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-slate-900 truncate">{testimonial.name}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {testimonial.company || testimonial.role}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Video Modal */}
      <Dialog open={videoModal.open} onOpenChange={(open) => setVideoModal({ open, url: null })}>
        <DialogContent className="max-w-4xl p-0 bg-black overflow-hidden">
          <button
            onClick={() => setVideoModal({ open: false, url: null })}
            className="absolute top-2 right-2 z-50 w-8 h-8 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center transition-colors"
          >
            <X className="h-5 w-5 text-white" />
          </button>
          {videoModal.url && (
            <iframe
              src={videoModal.url}
              className="w-full aspect-video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Testimonial Video"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
